// eslint-disable-next-line
import React, { createElement, useEffect } from 'react';
import clsx from 'clsx';
import { Box, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { orbitPreviewsStyles } from './styles';
import { loadZone } from 'shared/loadZoneSlice';
import { getConfig } from 'GeminiViewerComponent/features/config/configSlice';
import { getScenesOpen } from 'GeminiViewerComponent/features/globals/visibilitySlice';
import { getActiveAssetActiveZone } from 'GeminiViewerComponent/features/asset/assetSlice';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';

const OrbitPreviews = ({
    onAssetClick,
    onOtherAssetCategoryClick,
    onCategoryClick,
    zonesByCategory,
    otherAssetsByCategory,
    searchMenu,
    controlMenu,
}) => {
    const dispatch = useDispatch();

    const theme = useSelector(selectActiveTheme);

    const classes = orbitPreviewsStyles(theme);

    const config = useSelector(getConfig);
    const activeZone = useSelector(getActiveAssetActiveZone);
    const scenesOpen = useSelector(getScenesOpen);

    function handleClick(zone) {
        dispatch(loadZone({ zoneId: zone.zone_id }));
    }

    useEffect(() => {
        if (activeZone) {
            let domElement = document.getElementById(`zone_${activeZone.zone_id}`);
            if (domElement) {
                domElement.scrollIntoView();
            }
        }
    }, [activeZone]);

    const activeZoneId = activeZone == null ? '' : activeZone.zone_id;

    const categoryNames = Object.keys(zonesByCategory ?? {});
    const otherAssetNames = Object.keys(otherAssetsByCategory ?? {});

    const blocks = [];

    // Current asset categories first
    for (const categoryIdx in categoryNames) {
        const categoryName = categoryNames[categoryIdx];
        let category = zonesByCategory[categoryName];

        const zoneComponents = [];
        for (const idx in category.zones ?? []) {
            const zone = category.zones[idx];
            const previewUrl = zone.equirect_image_preview_url
                ? zone.equirect_image_preview_url
                : zone.flat_image_preview_url
                ? zone.flat_image_preview_url
                : zone.pano_id && config.paths.BASE_STORE_URL
                ? config.paths.BASE_STORE_URL + `tiles/${zone.pano_id}/1/f/0/0.jpg`
                : '';

            const zoneComponent = (
                <div
                    id={`viewer_title_zone-nav-category-${categoryIdx}_item-${idx}`}
                    key={`cat_${categoryIdx}_zone_${idx}`}
                    data-zone-id={zone?.zone_id}
                    className={clsx(
                        classes.scenePreviewContainer,
                        `viewer_title_zone-nav-${categoryIdx}_item`,
                        'viewer_title_zone-nav_item'
                    )}
                    style={{
                        backgroundColor: zone?.asset_name && '#abcad0',
                        color: zone?.asset_name && '#000000',
                    }}
                    onClick={() => {
                        handleClick(zone);
                    }}
                >
                    <Tooltip title={zone.display_name}>
                        <Box style={{ width: '100%' }}>
                            {previewUrl ? (
                                <img
                                    className={clsx(classes.sceneImage, {
                                        [classes.sceneActive]: zone.zone_id === activeZoneId,
                                    })}
                                    src={previewUrl}
                                    alt={`${previewUrl}`.toLowerCase()}
                                />
                            ) : (
                                <div
                                    className={clsx(classes.sceneNoImage, {
                                        [classes.sceneActive]: zone.zone_id === activeZoneId,
                                    })}
                                >
                                    No Image
                                </div>
                            )}
                            <div
                                className={clsx(classes.scenePreviewTitle, {
                                    [classes.scenePreviewTitleActive]: zone.zone_id === activeZoneId,
                                })}
                                style={{
                                    backgroundColor: zone?.asset_name && '#abcad0',
                                    color: zone?.asset_name && '#000000',
                                }}
                            >
                                {zone.display_name}
                            </div>
                        </Box>
                    </Tooltip>
                </div>
            );

            zoneComponents.push(zoneComponent);
        }

        blocks.push(
            <div className={classes.categoryContainer} key={`cat_${categoryIdx}`}>
                <Tooltip title={categoryName}>
                    <div
                        id={`viewer_title_zone-nav-category-${categoryIdx}}`}
                        className={clsx(classes.categoryTitle, 'viewer_title_zone-nav-category')}
                        style={{ ...category?.category_color }}
                        onClick={() => onCategoryClick(categoryName)}
                    >
                        {categoryName}
                    </div>
                </Tooltip>
                <div
                    className={clsx(classes.categoryZones, {
                        categoryOpen: category.open,
                    })}
                >
                    {zoneComponents}
                </div>
            </div>
        );
    }

    // Other assets section
    for (const assetIdx in otherAssetNames) {
        const assetName = otherAssetNames[assetIdx];
        const asset = otherAssetsByCategory[assetName];
        const categoryNamesOfAsset = Object.keys(asset?.categories ?? {});

        const categoryBlocks = [];
        for (const cIdx in categoryNamesOfAsset) {
            const cName = categoryNamesOfAsset[cIdx];
            const cObj = asset.categories[cName];

            const zoneComponents = [];
            for (const idx in cObj.zones) {
                const zone = cObj.zones[idx];
                const previewUrl = zone.equirect_image_preview_url
                    ? zone.equirect_image_preview_url
                    : zone.flat_image_preview_url
                    ? zone.flat_image_preview_url
                    : zone.pano_id && config.paths.BASE_STORE_URL
                    ? config.paths.BASE_STORE_URL + `tiles/${zone.pano_id}/1/f/0/0.jpg`
                    : '';

                const zoneComponent = (
                    <div
                        id={`viewer_title_zone-nav-other-${assetIdx}_${cIdx}_item-${idx}`}
                        key={`other_${assetIdx}_cat_${cIdx}_zone_${idx}`}
                        data-zone-id={zone?.zone_id}
                        className={clsx(
                            classes.scenePreviewContainer,
                            `viewer_title_zone-nav-${assetIdx}_${cIdx}_item`,
                            'viewer_title_zone-nav_item'
                        )}
                        style={{
                            backgroundColor: zone?.asset_name && '#abcad0',
                            color: zone?.asset_name && '#000000',
                        }}
                        onClick={() => {
                            handleClick(zone);
                        }}
                    >
                        <Tooltip title={zone.display_name}>
                            <Box style={{ width: '100%' }}>
                                {previewUrl ? (
                                    <img
                                        className={clsx(classes.sceneImage, {
                                            [classes.sceneActive]: zone.zone_id === activeZoneId,
                                        })}
                                        src={previewUrl}
                                        alt={`${previewUrl}`.toLowerCase()}
                                    />
                                ) : (
                                    <div
                                        className={clsx(classes.sceneNoImage, {
                                            [classes.sceneActive]: zone.zone_id === activeZoneId,
                                        })}
                                    >
                                        No Image
                                    </div>
                                )}
                                <div
                                    className={clsx(classes.scenePreviewTitle, {
                                        [classes.scenePreviewTitleActive]: zone.zone_id === activeZoneId,
                                    })}
                                    style={{
                                        backgroundColor: zone?.asset_name && '#abcad0',
                                        color: zone?.asset_name && '#000000',
                                    }}
                                >
                                    {zone.display_name}
                                </div>
                            </Box>
                        </Tooltip>
                    </div>
                );

                zoneComponents.push(zoneComponent);
            }

            categoryBlocks.push(
                <div className={classes.categoryContainer} key={`other_asset_${assetIdx}_cat_${cIdx}`}>
                    <Tooltip title={cName}>
                        <div
                            id={`viewer_title_zone-nav-other-asset-category-${assetIdx}_${cIdx}`}
                            className={clsx(classes.categoryTitle, 'viewer_title_zone-nav-category')}
                            style={{ ...cObj?.category_color }}
                            onClick={() => onOtherAssetCategoryClick(assetName, cName)}
                        >
                            {cName}
                        </div>
                    </Tooltip>
                    <div
                        className={clsx(classes.categoryZones, {
                            categoryOpen: cObj.open,
                        })}
                    >
                        {zoneComponents}
                    </div>
                </div>
            );
        }

        blocks.push(
            <div className={classes.categoryContainer} key={`other_asset_${assetIdx}`}>
                <Tooltip title={assetName}>
                    <div
                        id={`viewer_title_zone-nav-other-asset-${assetIdx}`}
                        className={clsx(classes.categoryTitle, 'viewer_title_zone-nav-asset')}
                        style={{ ...asset?.asset_color }}
                        onClick={() => onAssetClick(assetName)}
                    >
                        {assetName}
                    </div>
                </Tooltip>
                <div
                    className={clsx(classes.categoryZones, {
                        categoryOpen: asset.open,
                    })}
                >
                    {categoryBlocks}
                </div>
            </div>
        );
    }
    let menu = 0;
    if (searchMenu === true) menu++;
    if (controlMenu === true) menu++;
    return (
        <div className={classes.scenePreviewsContainer} style={{ top: `${menu * 40}px` }}>
            <div
                className={clsx(classes.scenePreviews, {
                    [classes.sceneOpen]: scenesOpen,
                })}
            >
                {blocks}
            </div>
        </div>
    );
};

export { OrbitPreviews };
